import { Box, Icon, IconButton, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { EllipsisVerticalIcon, FileSearchIcon, SquarePenIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import dataUpdatedIcon from '../../../../assets/images/data-updated-icon.svg';
import { useCaseApi } from '../../../../services/api/useCase.api';
import { Case } from '../../../../types/Case/Case.type';
import DeleteCaseModal from '../DeleteCaseModal';
import RenameCaseModal from '../RenameCaseModal';

export const ListCaseItem = ({ caseData, activeSort }: { caseData: Case; activeSort: string }) => {
  const queryClient = useQueryClient();
  const { renameCase, deleteCase } = useCaseApi();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const menuOpen = Boolean(anchorEl);

  const { mutate: renameCaseMutation, isPending: isRenaming } = useMutation({
    mutationKey: ['renameCase'],
    mutationFn: ({ id, name }: { id: number; name: string }) => renameCase(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userCases'], exact: false });
      handleRenameModalClose();
    },
  });

  const { mutate: deleteCaseMutation, isPending: isDeleting } = useMutation({
    mutationKey: ['deleteCase'],
    mutationFn: (id: number) => deleteCase(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userCases'], exact: false });
      handleDeleteModalClose();
    },
  });

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRenameOptionClick = () => {
    setRenameModalOpen(true);
    handleMenuClose();
  };

  const handleRenameModalClose = () => {
    setRenameModalOpen(false);
  };

  const handleRenameConfirm = (newName: string) => {
    if (caseData.id) {
      renameCaseMutation({ id: caseData.id, name: newName });
    }
  };

  const handleDeleteOptionClick = () => {
    setDeleteModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (caseData.id) {
      deleteCaseMutation(caseData.id);
    }
  };

  return (
    <>
      <Link
        to={`/case-details/${caseData.id}`}
        style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', width: '100%' }}
      >
        <Icon
          sx={{
            height: '100%',
            width: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            color: 'secondary.main',
          }}
        >
          <FileSearchIcon />
        </Icon>
        <ListItemText
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          slotProps={{
            primary: {
              sx: {
                fontSize: '16px',
                fontWeight: 600,
                color: '#2d3748',
                flex: 1,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                marginRight: '12px',
                width: '120px',
              },
            },
            secondary: {
              sx: {
                fontSize: '14px',
                color: '#a0aec0',
                flex: 1,
                width: '330px;',
                textOverflow: 'ellipsis',
              },
            },
          }}
          primary={caseData.name}
          title={`${caseData.name} - ${caseData.statutes.map((statute) => statute.display_name).join(', ')}`}
          secondary={caseData.statutes.map((statute) => statute.display_name).join(', ')}
        />
        <Typography
          sx={{
            fontSize: '14px',
            color: '#a0aec0',
            flex: 1,
            width: '150px',
          }}
        >
          {activeSort === 'created_at:desc'
            ? formatDistanceToNow(new Date(caseData.created_at), {
                addSuffix: true,
              })
            : formatDistanceToNow(new Date(caseData.updated_at), {
                addSuffix: true,
              })}
        </Typography>
      </Link>
      {caseData.has_disparities_to_recalculate || (caseData.deleted_statute_names?.length ?? 0) > 0 ? (
        <IconButton size='small' disabled sx={{ background: '#FEEBCB !important', color: '#ED8936 !important' }}>
          <img src={dataUpdatedIcon} alt='Data updated icon' width={22} height={22} />
        </IconButton>
      ) : (
        <Box sx={{ width: '32px', height: '32px' }}></Box>
      )}
      <IconButton
        aria-label='options'
        aria-haspopup='true'
        aria-controls={menuOpen ? 'options-menu' : undefined}
        aria-expanded={menuOpen ? 'true' : undefined}
        onClick={handleMenuClick}
        sx={{ color: 'primary.dark' }}
      >
        <EllipsisVerticalIcon />
      </IconButton>
      <Menu
        id='options-menu'
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            style: {
              width: '20ch',
            },
          },
          list: { 'aria-labelledby': 'options-button' },
        }}
      >
        <MenuItem onClick={handleRenameOptionClick}>
          <SquarePenIcon style={{ marginRight: '16px', width: '20px' }} />
          Rename
        </MenuItem>
        <MenuItem onClick={handleDeleteOptionClick} sx={{ color: 'error.main' }}>
          <Trash2Icon style={{ marginRight: '16px', width: '20px' }} />
          Delete
        </MenuItem>
      </Menu>
      <RenameCaseModal
        open={renameModalOpen}
        onClose={handleRenameModalClose}
        currentCaseName={caseData.name}
        onRename={handleRenameConfirm}
        loading={isRenaming}
      />
      <DeleteCaseModal
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        currentCaseName={caseData.name}
        onDelete={handleDeleteConfirm}
        loading={isDeleting}
      />
    </>
  );
};
